// src/components/shared/DynamicTable.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableHead, TableRow, TablePagination,
  TableSortLabel, Checkbox, Typography, Stack,
} from '@mui/material';
import DoubleScrollbar from 'react-double-scrollbar';
import FilterViewButton from './FilterViewButton';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { useLocalization } from 'src/context/LocalizationContext';

const hoverColor = '#f5f5f5';
const hoverStyles = { '&:hover': { backgroundColor: hoverColor, cursor: 'pointer' } };

const DynamicTable = ({
  data,
  headers,
  rowsPerPageOptions = [25, 50, 100],
  initialRowsPerPage = 25,
  initialOrder,
  initalOrderDirection,
  isOrdered,
  rowOnClick,
  selected,
  handleCheckboxChange,
  handleModal,
  mustBeShown = false,
  isMobile,
  children,
  rowStyles,
  handleSelectAll,
}) => {
  const { language } = useLocalization();
  const [columns, setColumns] = useState(headers || []);
  const [order, setOrder] = useState(initalOrderDirection);
  const [orderBy, setOrderBy] = useState(initialOrder);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState();
  const [page, setPage] = useState(0);

  // NEW: headers değişince kolonları güncelle (anında yansıma)
  useEffect(() => {
    if (!headers) return;
    if (isOrdered) {
      // 'No' kolonunu başa enjekte et
      const withoutNo = headers.filter(h => h.id !== 'no');
      setColumns([{ id: 'no', label: 'No', alignRight: false, sx: { width: 10 } }, ...withoutNo]);
    } else {
      setColumns(headers);
    }
  }, [headers, isOrdered]);

  // Mount anında da mevcut davranışı koru
  useEffect(() => {
    if (isOrdered) {
      setColumns((prev) => {
        // 'No' yoksa ekle
        const hasNo = prev.some(c => c.id === 'no');
        if (hasNo) return prev;
        return [{ id: 'no', label: 'No', alignRight: false, sx: { width: 10 } }, ...prev];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRequestSort = (property, sortByKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    if (sortByKey) setSortBy(sortByKey);
  };

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); };

  const getValue = (obj, path) => {
    const parts = path?.match(/([^[.\]])+/g);
    return parts?.reduce((o, key) => (o && o[key] ? o[key] : undefined), obj);
  };

  const sortedData = JSON.parse(JSON.stringify(data)).sort((a, b) => {
    const key = orderBy;
    const sortKey = sortBy || key;
    const column = columns.find((col) => col.id === orderBy);
    if (!column) return 0;

    const valueA = column.sortFunction ? column.sortFunction(a) : getValue(a, sortKey);
    const valueB = column.sortFunction ? column.sortFunction(b) : getValue(b, sortKey);

    if (valueA == null && valueB == null) return 0;
    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return order === 'asc' ? valueA - valueB : valueB - valueA;
    }
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return order === 'asc' ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    }
    return 0;
  });

  const getDeviceInfo = () => {
    const ua = window.navigator.userAgent;
    const isMobileDev = /Mobi|Android/i.test(ua);
    const isTablet = /Tablet|iPad/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    return { isDevice: isMobileDev || isTablet || isIOS || isAndroid };
  };
  const { isDevice } = getDeviceInfo();

  return (
    <>
      <Stack flexDirection={'row'} justifyContent={handleModal ? 'space-between' : 'end'} paddingLeft={3} paddingRight={3} paddingTop={3}>
        <Stack direction="row" spacing={2}>
          <>
            {isMobile ? null : handleModal ? (<FilterViewButton variant="contained" onClick={handleModal} />) : null}
            {children ? <div>{children}</div> : null}
            {mustBeShown && (
              <Typography style={{ marginBottom: '10px', color: '#646669', alignItems: 'center', display: 'flex' }}>
                <FmdGoodOutlinedIcon style={{ color: '#da3831' }} />
                {mustBeShown}
              </Typography>
            )}
          </>
        </Stack>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={data.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={language.components.DynamicTable.text}
        />
      </Stack>

      <DoubleScrollbar>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) =>
                column.id === 'checkbox' ? (
                  <TableCell key="checkbox" align="left" sx={{ backgroundColor: '#FFF1F3', ...column.sx }}>
                    <Checkbox
                      checked={selected?.length === data.length && data.length > 0}
                      indeterminate={selected?.length > 0 && selected?.length < data.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                ) : (
                  <TableCell
                    key={column.id}
                    sx={{ backgroundColor: '#FFF1F3', ...column.sx }}
                    align={column.align || 'left'}
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => (column.sortBy ? handleRequestSort(column.id, column.sortBy) : handleRequestSort(column.id, column.id))}
                      sx={{ fontWeight: 600, color: '#6B7280', fontFamily: 'Inter', fontSize: 12, lineHeight: '30px', letterSpacing: 0.5 }}
                    >
                      {column.label}
                    </TableSortLabel>
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
              <TableRow
                onClick={rowOnClick && isDevice ? () => rowOnClick(row) : null}
                style={rowStyles ? rowStyles(row) : {}}
                sx={rowOnClick && isDevice ? { ...hoverStyles, '&:last-child td, &:last-child th': { border: 0 } } : {}}
                hover
                key={index}
                tabIndex={-1}
              >
                {columns.map((column) => {
                  const value = row[column.id];
                  if (column.id === 'no') {
                    return <TableCell key="index" align="left">{page * rowsPerPage + index + 1}</TableCell>;
                  } else if (column.id === 'checkbox') {
                    return (
                      <TableCell key="checkbox" align="left">
                        <Checkbox checked={selected?.indexOf(row.id) !== -1} onChange={(e) => handleCheckboxChange(e, row.id, row)} />
                      </TableCell>
                    );
                  } else {
                    return (
                      <TableCell key={column.id} align={column.align || 'left'}>
                        {column.format ? column.format(value, row) : value}
                      </TableCell>
                    );
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DoubleScrollbar>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={language.components.DynamicTable.text}
      />
    </>
  );
};

export default DynamicTable;